import React, { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "@reduxjs/toolkit";
import styles from "./ReorderRedressLayer.module.css";
import { CustomButton } from "../../../../../../../common/components";
import { translate } from "../../../../../../../common/providers";
import {
	getReqPageContent,
	setReorderIndexDirection,
	setReorderOverlay,
	swapRedressmentContents,
} from "../../../../../slice/pdf/pdf-slice";
import { ROLES } from "../../../../../constants/constants";
import { RedressmentService, ReqViewService } from "../../../../../../../api";
import { useApi } from "../../../../../../../common/hooks";
import { setSelectedBlock } from "../../../../../slice/document/document-slice";

type Req = {
	informationId: number;
	rectangles: { contentId: number; order: number; page: number | null }[];
};
type Rectangle = {
	contentId: number;
	order: number;
};

const selectPageContents = createSelector(
	[(state) => state.srPdf.content, (_, pageStart) => pageStart],
	(content, pageStart) => {
		if (!pageStart) {
			return [];
		}
		const pages = new Set([pageStart - 1, pageStart, pageStart + 1]);
		return (content as { page: number; req: Req[] }[])
			.filter((p) => pages.has(p.page))
			.map(({ page, req }) => ({ page, req }));
	}
);

const ReorderRedressLayer: React.FC = () => {
	const reorderRedressmentFocusActive = useSelector(({ srPdf }) => srPdf.reorderRedressmentFocusActive);
	const reorderContentOrder = useSelector(({ srPdf }) => srPdf.reorderContentOrder);
	const reorderRectangles = useSelector(({ srPdf }) => srPdf.reorderRectangles);
	const docId = useSelector(({ srDocument }) => srDocument.documentId);
	const pageContents = useSelector((state) => selectPageContents(state, reorderRedressmentFocusActive));
	const mode = useSelector(({ srDocument }) => srDocument.mode);
	const { call: getContents } = useApi(ReqViewService.getContents);
	const { call: swapContentOrder } = useApi(RedressmentService.swapContentOrder);
	const dispatch = useDispatch();
	useEffect(() => {
		if (mode !== ROLES.EDITOR.mode) {
			dispatch(setReorderOverlay(null));
		}
	}, [mode, dispatch]);

	const loadInformation = useCallback(
		async () => {
			if (!reorderRedressmentFocusActive || !docId) return;
			try {
				await Promise.all(
					pageContents.map(async (pageContent) => {
						if (pageContent.req === undefined) {
							const content = await getContents({ docId }, { page: pageContent.page });
							dispatch(getReqPageContent({ content, page: pageContent.page }));
						}
					})
				);
			} catch (err) {
				console.error("Error loading information:", err);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[docId, dispatch, getContents, reorderRedressmentFocusActive]
	);
	useEffect(() => {
		if (reorderRedressmentFocusActive) {
			loadInformation();
		}
	}, [loadInformation, reorderRedressmentFocusActive]);

	const handleCancel = () => {
		if (reorderContentOrder === null) {
			dispatch(setReorderOverlay(null));
		} else {
			dispatch(setReorderIndexDirection({ rectangle: null, above: false }));
		}
	};

	const handleApply = () => {
		if (reorderRectangles.every((r: Rectangle) => !!r.contentId) && !!docId) {
			swapContentOrder(
				{ docId },
				{
					contentId1: reorderRectangles[0].contentId,
					contentId2: reorderRectangles[1].contentId,
				}
			)
				.then(() => dispatch(swapRedressmentContents(reorderRectangles)))
				.catch(console.error);
		}
	};
	const handleFinalize = () => {
		handleApply();
		dispatch(setReorderOverlay(null));
		dispatch(setSelectedBlock(null));
	};
	if (!reorderRedressmentFocusActive) {
		return null;
	}
	return (
		<div className={styles.main}>
			<CustomButton
				className={styles.buttonLeft}
				color="secondary"
				size="sm"
				variant="outlined"
				onClick={handleCancel}
			>
				{translate("common:btn.cancel")}
			</CustomButton>
			<CustomButton
				className={styles.buttonLeft}
				disabled={!reorderContentOrder}
				size="sm"
				variant="outlined"
				onClick={handleApply}
			>
				{translate("smart-review.redress-reorder.submit")}
			</CustomButton>
			<CustomButton
				className={styles.buttonLeft}
				disabled={!reorderContentOrder}
				size="sm"
				variant="contained"
				onClick={handleFinalize}
			>
				{translate("common:btn.validate")}
			</CustomButton>
		</div>
	);
};

export default ReorderRedressLayer;
